/** A link with the page's query appended ("schoolSlug=clay&theme=x"): after
 * any query the link already has, before any #fragment. Pure. */
export function withQuery(href: string, query = ""): string {
  const hash = href.indexOf("#");
  const path = hash === -1 ? href : href.slice(0, hash);
  const fragment = hash === -1 ? "" : href.slice(hash);
  // A key the link already carries keeps its own value (an order's return
  // address built with the link query must not get it twice).
  const qIndex = path.indexOf("?");
  const present = new Set(qIndex === -1 ? [] : [...new URLSearchParams(path.slice(qIndex + 1)).keys()]);
  const extra = query
    .trim()
    .replace(/^[?&]+/, "")
    .split("&")
    .filter((pair) => pair && !present.has(decodeKey(pair)))
    .join("&");
  if (!extra) return href;
  const joiner = !path.includes("?") ? "?" : /[?&]$/.test(path) ? "" : "&";
  return `${path}${joiner}${extra}${fragment}`;
}

function decodeKey(pair: string): string {
  const key = pair.split("=")[0];
  try {
    return decodeURIComponent(key.replace(/\+/g, " "));
  } catch {
    return key;
  }
}

/** The flows' links to each other and to the school's site (`base` "": the
 * page's own address) or under `base`, each carrying `linkQuery` (the
 * website app's `schoolSlug`/`theme` on preview hosts, which a link without
 * them would lose). Pure. */
export function bookingLinks(base = "", linkQuery = "") {
  const root = base.replace(/\/+$/, "");
  const to = (path: string) => withQuery(`${root}${path}`, linkQuery);
  return {
    manage: (bookingId: string) => to(`/bookings/manage/${bookingId}`),
    cancel: (bookingId: string) => to(`/bookings/cancel/${bookingId}`),
    reschedule: (bookingId: string) => to(`/bookings/reschedule/${bookingId}`),
    review: (bookingId: string) => to(`/bookings/review/${bookingId}`),
    bundleDates: (bookingId: string) => to(`/bookings/bundle/${bookingId}`),
    bundle: (bundleId: string) => to(`/bundles/manage/${bundleId}`),
    event: (classSlug: string) => to(`/event/${classSlug}`),
    home: () => to("/"),
    // v1's address: on a switched-on host it redirects to v2's /classes
    // only when that page exists, and serves v1's list otherwise.
    events: () => to("/events"),
    contact: () => to("/contact"),
    profile: () => to("/profile"),
    terms: () => to("/terms"),
    // The payment pages' (plans/websitev2-parity-m4-payment-links.md).
    /** A product's page (v1's Buy Again: /<school>/products/… redirects
     * to the school's /product/<slug>). */
    product: (productSlug: string) => to(`/product/${productSlug}`),
    /** Any path on the school's site ("/giftcard/receipt/x?y=1#z"), e.g.
     * where an order's processing sends the customer on. */
    site: (path: string) => to(path.startsWith("/") ? path : `/${path}`),
    /** A path on the page's own host, never under `base`, with the link
     * query: where Stripe sends the customer back to (the page they paid
     * on, so a 3-D Secure or bank redirect lands back on this site). */
    page: (path: string) => withQuery(path.startsWith("/") ? path : `/${path}`, linkQuery),
  };
}

export type BookingLinks = ReturnType<typeof bookingLinks>;

/** The marketplace's address for a path, as v1 sends an OBBY booking there. */
export const marketplaceLink = (marketplaceUrl: string, path: string) =>
  `${marketplaceUrl.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
